import { FC, useState } from "react";
import { PlainI18nProps } from "shared/types/i18n";
import { twMerge } from "tailwind-merge";

import { Text } from "./text";
import { EmptyWrapper } from "./util/empty-wrapper";
import { i18nPropsHaveContent } from "../i18n/i18n-props-have-content";
import { useT } from "../i18n/use-t";

export type TooltipProps = {
  text?: PlainI18nProps;
  tooltipPosition?:
    | "tooltip-right"
    | "tooltip-top"
    | "tooltip-bottom"
    | "tooltip-left";
} & JSX.IntrinsicElements["div"];

export const Tooltip: FC<TooltipProps> = ({
  className,
  text,
  children,
  tooltipPosition = "tooltip",
  ...rest
}) => {
  const t = useT();
  return i18nPropsHaveContent(text) ? (
    <div
      {...rest}
      className={twMerge(tooltipPosition, className)}
      data-tip={t(text)}
    >
      {children}
    </div>
  ) : (
    <EmptyWrapper>{children}</EmptyWrapper>
  );
};

interface MultiLineTooltipProps extends TooltipProps {
  tooltipClassName?: string;
}

export const MultiLineTooltip: FC<MultiLineTooltipProps> = ({
  text,
  children,
  className,
  tooltipClassName,
  ...rest
}) => {
  const [isHovering, setIsHovering] = useState(false);

  return i18nPropsHaveContent(text) ? (
    <div
      {...rest}
      onPointerEnter={() => setIsHovering(true)}
      onPointerLeave={() => setIsHovering(false)}
    >
      {children}
      {isHovering && (
        <Text
          className={twMerge(
            "absolute z-10 flex w-max max-w-[280px] rounded-lg bg-slate-200 p-2",
            tooltipClassName,
          )}
          {...text}
        />
      )}
    </div>
  ) : (
    <EmptyWrapper>{children}</EmptyWrapper>
  );
};
